import {useCallback} from 'react';

import {speculatorT} from '@/lib/types/speculator.types';

import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';
import {
  BaseContextT,
  createAppContext,
  createAppHook,
  createAppProvider,
  createProviderConfig,
  useBaseContext,
} from '@/providers/components/baseProvider';

// Config
const {table, columns, statsView} = createProviderConfig({
  table: 'Speculator',
  statsView: 'speculator_stats',
});

type TableT = typeof table;
type ColumnsT = typeof columns;
type StatsViewT = typeof statsView;
type CustomContextT = {
  getSpeculator: (id: number) => Promise<speculatorT>;
};

// Context
type SpeculatorsContextT = BaseContextT<TableT, StatsViewT, ColumnsT, CustomContextT>;
const SpeculatorsContext = createAppContext<SpeculatorsContextT>(table);

// Provider
const ContextProvider = ({children}: {children: React.ReactNode}) => {
  const {isAdministrator} = useAuth();
  const baseContext = useBaseContext<TableT, ColumnsT, StatsViewT>();

  /*******************************************************************
   * Gets a speculator by ID from the database.
   * @param {number} id - The ID of the speculator.
   * @returns {Promise<speculatorT>} - The speculator.
   * @throws {Error} - If there's an error in getting the speculator.
   ********************************************************************/
  const getSpeculator = useCallback(
    async (id: number) => {
      if (!isAdministrator) throw new Error('Error getting speculator');
      try {
        const {data, error} = await SupabaseInstance.supabase
          .from('Speculator')
          .select()
          .eq('speculator_id', id)
          .single();
        if (!data || error) throw new Error('Speculator not found');
        return data;
      } catch (error) {
        throw new Error((error as Error).message);
      }
    },
    [isAdministrator]
  );

  return (
    <SpeculatorsContext.Provider
      value={{
        ...baseContext,
        getSpeculator,
      }}
    >
      {children}
    </SpeculatorsContext.Provider>
  );
};

// Add this to the AppProviders.tsx file
export const SpeculatorsProvider = createAppProvider({
  table,
  columns,
  statsView,
  ContextProvider,
});

// Hook
export const useSpeculators = createAppHook<TableT, StatsViewT, ColumnsT, CustomContextT>(SpeculatorsContext);
